import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Checkbox, CircularProgress, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, Select } from "@material-ui/core";
import ActionDialog from "../../ActionDialog";
import { editAgreementInvestment, getInvestments } from "../../../../actions";
import { useManagementAction } from "../../../../hooks/useManagementAction";

const ChangeSegment = ({ open, handleCloseDialog, user, fetchUser }) => {
  const { handleManagementAction } = useManagementAction();

  const initialSegment = user.contractAgent.investment;
  const defaultSegment = user.contractAgent.contractAgentClass.investment;
  const [loadingState, setLoadingState] = useState("fetching");
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(() => initialSegment);
  const [useDefault, setUseDefault] = useState(user.contractAgent.defaultInvestment);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const onFinish = () => {
    if (!isMountedRef.current) return;
    
    setLoadingState("idle");
    handleCloseDialog();
  }

  const handleSubmit = async () => {
    setLoadingState("submitting");
    await handleManagementAction({
      body: { editedUserId: user.id, investmentId: selectedSegment?.id || null },
      managementAction: editAgreementInvestment,
      onComplete: fetchUser,
      onFinish,
      successMessage: "app.edit.sucesfully",
    });
  };

  const handleClose = () => {
    setSelectedSegment(initialSegment);
    handleCloseDialog();
  };

  useEffect(() => {
    setLoadingState("fetching");
    const fetchSegments = async () => {
      const segmentsData = await getInvestments();
      setSegments(segmentsData);
      setLoadingState("idle");
    };
    fetchSegments();
  }, []);

  useEffect(() => {
    setSelectedSegment(useDefault ? null : initialSegment);
  }, [useDefault, initialSegment]);

  const getRenderValue = () => {
    return useDefault ? defaultSegment?.description : selectedSegment?.description;
  };

  return (
    <ActionDialog
      title="app.edit"
      open={open}
      onAccept={handleSubmit}
      onClose={handleClose}
      textConfirm="app.edit"
      maxWidth="xs"
      disableConfirmButton={["fetching", "submitting"].includes(loadingState)}
      textToConfirmation={"app.confirm.changeSegment"}
    >
      {["submitting", "idle"].includes(loadingState) && (
        <>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
          >
            <InputLabel
              id="segment-label"
              shrink
            >
              <FormattedMessage id="app.manager.segment" />
            </InputLabel>

            <Select
              labelId="segment-label"
              value={selectedSegment ?? defaultSegment}
              renderValue={getRenderValue}
              notched
              label={<FormattedMessage id="app.manager.segment" />}
              onChange={(e) => setSelectedSegment(e.target.value)}
              disabled={["fetching", "submitting"].includes(loadingState) || useDefault}
            >
              {segments.map((s) => (
                <MenuItem
                  key={s.id}
                  value={s}
                >
                  <ListItemText primary={s?.description} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            disabled={["submitting"].includes(loadingState)}
            control={
              <Checkbox
                checked={useDefault}
                onChange={() => setUseDefault((prev) => !prev)}
                color="primary"
              />
            }
            label={<FormattedMessage id="app.manager.useDefault" />}
          />
        </>
      )}

      {["fetching", "submitting"].includes(loadingState) && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <CircularProgress />
        </div>
      )}
    </ActionDialog>
  );
};

export default ChangeSegment;
